"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";

interface BoundaryCombineActionsContextValue {
  combineSelectedBoundaries: () => Promise<void>;
  setCombineSelectedBoundaries: (fn: (() => Promise<void>) | null) => void;
  showCombineButton: boolean;
  setShowCombineButton: (value: boolean) => void;
}

const BoundaryCombineActionsContext =
  createContext<BoundaryCombineActionsContextValue | null>(null);

export function useBoundaryCombineActionsOptional() {
  return useContext(BoundaryCombineActionsContext);
}

export function useSetBoundaryCombineActions() {
  const context = useContext(BoundaryCombineActionsContext);
  if (!context) {
    return null;
  }
  return {
    setCombineSelectedBoundaries: context.setCombineSelectedBoundaries,
    setShowCombineButton: context.setShowCombineButton,
  };
}

export const BoundaryCombineActionsProvider: FC<PropsWithChildren> = ({ children }) => {
  const combineSelectedBoundariesRef = useRef<(() => Promise<void>) | null>(null);
  const [showCombineButton, setShowCombineButtonState] = useState(false);

  const combineSelectedBoundaries = useCallback(async () => {
    await combineSelectedBoundariesRef.current?.();
  }, []);

  const setCombineSelectedBoundaries = useCallback((fn: (() => Promise<void>) | null) => {
    combineSelectedBoundariesRef.current = fn;
  }, []);

  const setShowCombineButton = useCallback((shouldShow: boolean) => {
    setShowCombineButtonState(shouldShow);
  }, []);

  const value = useMemo<BoundaryCombineActionsContextValue>(
    () => ({
      combineSelectedBoundaries,
      setCombineSelectedBoundaries,
      showCombineButton,
      setShowCombineButton,
    }),
    [combineSelectedBoundaries, setCombineSelectedBoundaries, showCombineButton, setShowCombineButton],
  );

  return (
    <BoundaryCombineActionsContext.Provider value={value}>
      {children}
    </BoundaryCombineActionsContext.Provider>
  );
};
